import React, { useEffect, useRef, useState, useMemo } from "react";

type ProfileTab =
  | "Overview"
  | "Personal Details"
  | "Courses"
  | "Progress"
  | "Settings";

type StudentInfo = {
  fullName: string;
  email: string;
  phone: string;
  gender: string;
  dob: string;
  studentClass: string;
  course: string;
  city: string;
  state: string;
  guardianName: string;
  guardianPhone: string;
};

const tabs: ProfileTab[] = [
  "Overview",
  "Personal Details",
  "Courses",
  "Progress",
  "Settings",
];

// const PROFILE_API = "http://localhost:9090/api/profile";
// const COURSES_API = "http://localhost:9090/api/courses";
// const ENROLLMENTS_API = "http://localhost:9091/api/enrollments/by-email";

const PROFILE_API = "https://node-api.mediitacademy.com/api/profile";
const COURSES_API = "https://node-api.mediitacademy.com/api/courses";
const ENROLLMENTS_API = "https://api.mediitacademy.com/api/enrollments/by-email";

const StudentProfile: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ProfileTab>("Overview");
  const [profileImage, setProfileImage] = useState<string>("");
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [allCourses, setAllCourses] = useState<any[]>([]);
  const [myEnrollments, setMyEnrollments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [student, setStudent] = useState<StudentInfo>({
    fullName: "",
    email: "",
    phone: "",
    gender: "Female",
    dob: "",
    studentClass: "Class 10",
    course: "",
    city: "",
    state: "",
    guardianName: "",
    guardianPhone: "",
  });

  const fetchProfile = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/login";
      return null;
    }

    try {
      const response = await fetch(PROFILE_API, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        const updatedStudent = {
          fullName: data.full_name || data.fullName || data.name || "",
          email: data.email || "",
          phone: data.mobile_number || data.mobileNumber || data.phone || "",
          gender: data.gender || "Female",
          dob: data.dob ? data.dob.split("T")[0] : "",
          city: data.city || "",
          state: data.state || "",
          guardianName: data.guardian_name || data.guardianName || "",
          guardianPhone: data.guardian_phone || data.guardianPhone || "",
          studentClass: data.school || data.studentClass || data.class || "Class 10",
          course: data.course || "",
        };

        setStudent(updatedStudent);

        if (data.profile_image) {
          // setProfileImage(`http://localhost:9090${data.profile_image}`);
          setProfileImage(`https://node-api.mediitacademy.com${data.profile_image}`);
        }

        return updatedStudent;
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
    return null;
  };

  const fetchCourses = async () => {
    try {
      const response = await fetch(COURSES_API);
      if (response.ok) {
        const data = await response.json();
        setAllCourses(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
    }
  };

  const fetchMyEnrollments = async (email: string) => {
    if (!email) return;
    try {
      const response = await fetch(`${ENROLLMENTS_API}/${encodeURIComponent(email)}`);
      if (response.ok) {
        const data = await response.json();
        setMyEnrollments(Array.isArray(data) ? data : [data]);
      }
    } catch (error) {
      console.error("Error fetching enrollments:", error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const profile = await fetchProfile();
      if (profile) {
        await Promise.allSettled([
          fetchCourses(),
          fetchMyEnrollments(profile.email)
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleChange = (field: keyof StudentInfo, value: string) => {
    setStudent((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleProfileImageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select a valid image file.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert("Image size should be less than 2MB.");
      return;
    }

    setSelectedImageFile(file);

    const reader = new FileReader();
    reader.onload = () => {
      setProfileImage(String(reader.result));
    };

    reader.readAsDataURL(file);
  };

  const saveProfile = async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      alert("Session expired. Please login again.");
      return;
    }

    const formData = new FormData();

    Object.entries(student).forEach(([key, value]) => {
      formData.append(key, value);
    });

    if (selectedImageFile) {
      formData.append("profileImage", selectedImageFile);
    }

    try {
      const response = await fetch(PROFILE_API, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        alert("Profile updated successfully!");
      } else {
        const errorData = await response.json();
        alert(`Failed to update profile: ${errorData.error || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("An error occurred while saving the profile.");
    }
  };

  const logoutUser = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("student");
    localStorage.removeItem("user");
    localStorage.removeItem("studentId");
    localStorage.removeItem("userId");
    localStorage.removeItem("id");
    localStorage.removeItem("studentName");
    localStorage.removeItem("name");
    window.location.href = "/login";
  };

  const activeEnrollment = useMemo(() => {
    return myEnrollments.find((e) => {
      const status = String(e.status || e.paymentStatus || "").toUpperCase().trim();
      return e.courseAccessEnabled || e.accessEnabled || ["APPROVED", "PAID", "SUCCESS"].includes(status);
    });
  }, [myEnrollments]);

  const activeEnrolledCourse = useMemo(() => {
    if (activeEnrollment) {
      return allCourses.find(c => c.id === activeEnrollment.courseId || c.id === activeEnrollment.course_id) || null;
    }
    if (student.course) {
      return allCourses.find(
        (course) =>
          String(course.title || "").toLowerCase().trim() ===
          String(student.course || "").toLowerCase().trim()
      ) || null;
    }
    return null;
  }, [activeEnrollment, allCourses, student.course]);

  const profileInitial = student.fullName
    ? student.fullName.charAt(0).toUpperCase()
    : "S";

  const profileCompletion = [
    student.fullName,
    student.email,
    student.phone,
    student.gender,
    student.dob,
    student.studentClass,
    student.city,
    student.state,
    student.guardianName,
    student.guardianPhone,
  ].filter(Boolean).length;

  const completionPercent = Math.round((profileCompletion / 10) * 100);

  if (loading) {
    return <ProfileLoading />;
  }

  return (
    <div className="min-h-screen bg-[#f6f9ff] text-[#111827] font-['Inter']">
      <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/95 px-4 py-3 shadow-sm backdrop-blur-xl sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => (window.location.href = "/student-dashboard")}
              className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-xl font-black text-blue-700 hover:bg-blue-100"
              title="Back to dashboard"
            >
              ←
            </button>

            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-blue-700">
                Student Profile
              </p>
              <h1 className="text-xl font-black text-slate-950 sm:text-2xl">
                Manage Profile
              </h1>
            </div>
          </div>

          <div className="hidden items-center gap-2 lg:flex">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`rounded-2xl px-4 py-2 text-sm font-black transition ${
                  activeTab === tab
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-slate-50 text-slate-600 hover:bg-blue-50 hover:text-blue-700"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={saveProfile}
              className="rounded-2xl bg-blue-600 px-4 py-3 text-sm font-black text-white hover:bg-blue-700"
            >
              Save
            </button>

            <button
              onClick={logoutUser}
              className="rounded-2xl bg-red-50 px-4 py-3 text-sm font-black text-red-600 hover:bg-red-100"
            >
              Logout
            </button>
          </div>
        </div>

        <div className="mx-auto mt-3 flex max-w-[1500px] gap-2 overflow-x-auto pb-1 lg:hidden">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`shrink-0 rounded-2xl px-4 py-2 text-sm font-black transition ${
                activeTab === tab
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-slate-50 text-slate-600 hover:bg-blue-50 hover:text-blue-700"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] p-4 sm:p-6 lg:p-8">
        <section className="relative overflow-hidden rounded-[42px] bg-gradient-to-r from-[#efe2ff] via-[#dff6ff] to-[#d8f8ff] p-5 shadow-sm sm:p-8">
          <div className="absolute -left-16 top-24 h-32 w-32 rounded-full bg-sky-400" />
          <div className="absolute -right-10 bottom-8 h-24 w-24 rounded-full bg-pink-500" />
          <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-white/40 blur-3xl" />

          <div className="relative grid gap-8 xl:grid-cols-[360px_1fr_300px] xl:items-center">
            <div className="flex justify-center xl:justify-start">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="group relative h-[230px] w-[230px] rounded-full bg-gradient-to-br from-sky-400 via-violet-600 to-pink-500 p-[10px] shadow-[0_28px_60px_rgba(59,130,246,0.25)] transition hover:scale-[1.02]"
              >
                <span className="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-white text-7xl font-black text-blue-700">
                  {profileImage ? (
                    <img
                      src={profileImage}
                      alt={student.fullName || "Student"}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    profileInitial
                  )}
                </span>

                <span className="absolute bottom-4 right-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-xl shadow-lg transition group-hover:scale-110">
                  📷
                </span>
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfileImageChange}
                className="hidden"
              />
            </div>

            <div>
              <p className="text-sm font-black uppercase tracking-[0.2em] text-blue-700">
                Welcome Back
              </p>

              <h2 className="mt-3 text-4xl font-black tracking-[-0.05em] text-slate-950 sm:text-6xl">
                {student.fullName || "Student Name"}
              </h2>

              <p className="mt-4 max-w-3xl text-sm font-semibold leading-6 text-slate-600 sm:text-base">
                Aapka profile, course access, progress, personal details aur
                learning activity yaha ek premium dashboard style me manage
                hoga.
              </p>

              <div className="mt-5 flex flex-wrap gap-3">
                <InfoPill label="Class" value={student.studentClass || "--"} />
                <InfoPill
                  label="Course"
                  value={activeEnrolledCourse?.title || student.course || "Demo Access"}
                />
                <InfoPill label="Profile" value={`${completionPercent}%`} />
              </div>
            </div>

            <div className="rounded-[30px] bg-white/70 p-5 shadow-sm backdrop-blur">
              <p className="text-xs font-black uppercase tracking-wide text-slate-500">
                Profile Completion
              </p>

              <div className="mt-4 flex justify-center">
                <RingStat value={completionPercent} label="Completed" />
              </div>

              <button
                onClick={() => setActiveTab("Personal Details")}
                className="mt-5 w-full rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white hover:bg-blue-600"
              >
                Complete Profile
              </button>
            </div>
          </div>
        </section>

        <section className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            icon="⏱"
            title="Study Time"
            value="0h"
            desc="Learning time tracked"
          />
          <MetricCard
            icon="📋"
            title="Assignments"
            value="0"
            desc="Assignments completed"
          />
          <MetricCard
            icon="📘"
            title="Courses"
            value={activeEnrolledCourse ? "1" : "0"}
            desc={activeEnrolledCourse?.title || "No active course"}
          />
          <MetricCard
            icon="🎯"
            title="Progress"
            value={`${completionPercent}%`}
            desc="Profile strength"
          />
        </section>

        <section className="mt-6">
          {activeTab === "Overview" && (
            <OverviewTab
              student={student}
              activeEnrolledCourse={activeEnrolledCourse}
              completionPercent={completionPercent}
              setActiveTab={setActiveTab}
            />
          )}

          {activeTab === "Personal Details" && (
            <PersonalDetailsTab
              student={student}
              handleChange={handleChange}
              saveProfile={saveProfile}
            />
          )}

          {activeTab === "Courses" && (
            <CoursesTab
              allCourses={allCourses}
              student={student}
              activeEnrolledCourse={activeEnrolledCourse}
              myEnrollments={myEnrollments}
            />
          )}

          {activeTab === "Progress" && (
            <ProgressTab completionPercent={completionPercent} />
          )}

          {activeTab === "Settings" && (
            <SettingsTab logoutUser={logoutUser} />
          )}
        </section>
      </main>
    </div>
  );
};

const ProfileLoading = () => {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <style>{`
        @keyframes yellowBarUpDown {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-16px); }
        }
        @keyframes yellowCircleStep {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(32px, -16px); }
          50% { transform: translate(64px, 0); }
          75% { transform: translate(96px, -16px); }
        }
      `}</style>

      <div className="relative flex h-40 w-44 items-end justify-center rounded-[34px] border border-slate-100 bg-white p-7">
        <div
          className="absolute left-7 top-8 h-5 w-5 rounded-full border-[5px] border-yellow-500 bg-yellow-400"
          style={{ animation: "yellowCircleStep 1.7s ease-in-out infinite" }}
        />

        <div className="flex h-full items-end gap-3">
          {[48, 78, 106, 134].map((height, index) => (
            <span
              key={height}
              className="block w-5 rounded-t-xl border-[5px] border-yellow-500 bg-yellow-400"
              style={{
                height,
                animation: "yellowBarUpDown 1.25s ease-in-out infinite",
                animationDelay: `${index * 140}ms`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const OverviewTab = ({
  student,
  activeEnrolledCourse,
  completionPercent,
  setActiveTab,
}: {
  student: StudentInfo;
  activeEnrolledCourse: any;
  completionPercent: number;
  setActiveTab: (tab: ProfileTab) => void;
}) => {
  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_420px]">
      <div className="space-y-6">
        <div className="rounded-[36px] bg-white p-6 shadow-sm">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <p className="text-sm font-black uppercase tracking-wide text-blue-600">
                Profile Summary
              </p>
              <h2 className="mt-2 text-3xl font-black tracking-[-0.04em] text-slate-950">
                Student Information
              </h2>
            </div>

            <button
              onClick={() => setActiveTab("Personal Details")}
              className="w-fit rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white hover:bg-blue-700"
            >
              Edit Details
            </button>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <InfoCard label="Full Name" value={student.fullName || "--"} />
            <InfoCard label="Email" value={student.email || "--"} />
            <InfoCard label="Mobile" value={student.phone || "--"} />
            <InfoCard
              label="Student Class"
              value={student.studentClass || "--"}
            />
            <InfoCard
              label="Course"
              value={activeEnrolledCourse?.title || student.course || "Demo Access"}
            />
            <InfoCard
              label="City / State"
              value={`${student.city || "--"} / ${student.state || "--"}`}
            />
          </div>
        </div>

        <div className="rounded-[36px] bg-white p-6 shadow-sm">
          <p className="text-sm font-black uppercase tracking-wide text-blue-600">
            Current Course
          </p>

          {activeEnrolledCourse ? (
            <div className="mt-5 rounded-[28px] bg-gradient-to-br from-blue-50 to-white p-5">
              <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
                <div>
                  <h3 className="text-2xl font-black text-slate-950">
                    {activeEnrolledCourse.title}
                  </h3>
                  <p className="mt-2 text-sm font-bold text-slate-500">
                    {activeEnrolledCourse.category || "Course"} • Mentor:{" "}
                    {activeEnrolledCourse.teacher || "Assigned Teacher"}
                  </p>
                </div>

                <span className="w-fit rounded-full bg-emerald-50 px-4 py-2 text-xs font-black text-emerald-700">
                  Active
                </span>
              </div>

              <button
                onClick={() => (window.location.href = "/student-dashboard")}
                className="mt-5 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white hover:bg-blue-700"
              >
                Continue Learning
              </button>
            </div>
          ) : (
            <div className="mt-5 rounded-[28px] border border-dashed border-slate-300 bg-[#f6f9ff] p-8 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-3xl shadow-sm">
                ✦
              </div>
              <h3 className="mt-4 text-xl font-black text-slate-950">
                Demo access active
              </h3>
              <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">
                Course subscribe karne ke baad yaha full course details,
                progress, notes aur live class access show hoga.
              </p>
              <button
                onClick={() => (window.location.href = "/courses")}
                className="mt-5 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white"
              >
                Explore Courses
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-6">
        <KarmaLeaderboard />
        <div className="rounded-[36px] bg-white p-6 shadow-sm">
          <p className="text-sm font-black uppercase tracking-wide text-blue-600">
            Learning Objective Mastery
          </p>

          <div className="mt-6 space-y-4">
            {[
              { label: "Profile Setup", value: completionPercent },
              { label: "Course Access", value: activeEnrolledCourse ? 100 : 20 },
              { label: "Live Classes", value: 0 },
              { label: "Tests Practice", value: 0 },
            ].map((item) => (
              <ProgressLine
                key={item.label}
                label={item.label}
                value={item.value}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const PersonalDetailsTab = ({
  student,
  handleChange,
  saveProfile,
}: {
  student: StudentInfo;
  handleChange: (field: keyof StudentInfo, value: string) => void;
  saveProfile: () => void;
}) => {
  return (
    <div className="rounded-[36px] bg-white p-6 shadow-sm">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="text-sm font-black uppercase tracking-wide text-blue-600">
            Personal Details
          </p>
          <h2 className="mt-2 text-3xl font-black tracking-[-0.04em] text-slate-950">
            Update Your Information
          </h2>
          <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-slate-500">
            Name, DOB, class, guardian aur location details yaha se update karein.
          </p>
        </div>

        <button
          onClick={saveProfile}
          className="rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white hover:bg-blue-700"
        >
          Save Changes
        </button>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <TextInput
          label="Full Name"
          value={student.fullName}
          onChange={(value) => handleChange("fullName", value)}
        />

        <TextInput
          label="Email Address"
          value={student.email}
          disabled
          onChange={(value) => handleChange("email", value)}
        />

        <TextInput
          label="Mobile Number"
          value={student.phone}
          disabled
          onChange={(value) => handleChange("phone", value)}
        />

        <SelectInput
          label="Gender"
          value={student.gender}
          options={["Female", "Male", "Other"]}
          onChange={(value) => handleChange("gender", value)}
        />

        <TextInput
          label="Date of Birth"
          type="date"
          value={student.dob}
          onChange={(value) => handleChange("dob", value)}
        />

        <SelectInput
          label="Student Class"
          value={student.studentClass}
          options={[
            "Class 7",
            "Class 8",
            "Class 9",
            "Class 10",
            "Class 11",
            "Class 12",
            "After 12",
          ]}
          onChange={(value) => handleChange("studentClass", value)}
        />

        <TextInput
          label="City"
          value={student.city}
          onChange={(value) => handleChange("city", value)}
        />

        <TextInput
          label="State"
          value={student.state}
          onChange={(value) => handleChange("state", value)}
        />

        <TextInput
          label="Guardian Name"
          value={student.guardianName}
          onChange={(value) => handleChange("guardianName", value)}
        />

        <TextInput
          label="Guardian Phone"
          value={student.guardianPhone}
          onChange={(value) => handleChange("guardianPhone", value)}
        />
      </div>
    </div>
  );
};

const CoursesTab = ({
  allCourses,
  student,
  activeEnrolledCourse,
  myEnrollments,
}: {
  allCourses: any[];
  student: StudentInfo;
  activeEnrolledCourse: any;
  myEnrollments: any[];
}) => {
  return (
    <div className="space-y-6">
      <PageHeader
        label="Courses"
        title="Available Programs"
        desc="Yaha se student apne courses dekh aur subscribe kar sakta hai."
      />

      {activeEnrolledCourse && (
        <div className="rounded-[36px] border border-emerald-100 bg-emerald-50 p-6">
          <p className="text-sm font-black uppercase tracking-wide text-emerald-700">
            Active Subscription
          </p>
          <h2 className="mt-2 text-2xl font-black text-slate-950">
            {activeEnrolledCourse.title}
          </h2>
          <p className="mt-2 text-sm font-semibold text-emerald-700">
            Is course ke live classes, recordings, notes aur tests unlock hain.
          </p>
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-2">
        {allCourses.map((course) => {
          
          const isOwned = myEnrollments.some((e: any) => {
             const status = String(e.status || e.paymentStatus || "").toUpperCase().trim();
             const isActive = e.courseAccessEnabled || e.accessEnabled || ["APPROVED", "PAID", "SUCCESS"].includes(status);
             return isActive && (e.courseId === course.id || e.course_id === course.id);
          });

          return (
            <div
              key={course.id}
              className={`rounded-[32px] p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-xl ${
                isOwned ? "border border-emerald-300 bg-emerald-50/20" : "bg-white"
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-black text-slate-950">
                    {course.title}
                  </h3>
                  <p className="mt-2 text-sm font-bold text-slate-500">
                    {course.category || "Course"}
                  </p>
                </div>

                <span
                  className={`rounded-full px-4 py-2 text-xs font-black ${
                    isOwned
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-blue-50 text-blue-700"
                  }`}
                >
                  {isOwned ? "Already Enrolled" : `₹${course.price || "—"}`}
                </span>
              </div>

              <p className="mt-5 rounded-2xl bg-blue-50 p-4 text-sm font-bold text-blue-700">
                Mentor: {course.teacher || "Assigned Teacher"}
              </p>

              <button
                onClick={() =>
                  (window.location.href = isOwned
                    ? "/student-dashboard"
                    : `/course-purchase/${course.id}`)
                }
                className={`mt-5 w-full rounded-2xl px-5 py-3 text-sm font-black text-white ${
                  isOwned ? "bg-emerald-600 hover:bg-emerald-700" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {isOwned ? "Continue Learning" : "Subscribe Now"}
              </button>
            </div>
          );
        })}

        {allCourses.length === 0 && (
          <div className="rounded-[30px] bg-white p-8 text-center text-sm font-bold text-slate-500 shadow-sm">
            Loading courses from database...
          </div>
        )}
      </div>
    </div>
  );
};

const ProgressTab = ({ completionPercent }: { completionPercent: number }) => {
  return (
    <div className="space-y-6">
      <PageHeader
        label="Progress"
        title="Learning Analytics"
        desc="Student ke tests, live classes aur progress reports yaha show honge."
      />

      <div className="grid gap-5 md:grid-cols-3">
        <StatCard title="Average Accuracy" value="0%" desc="No tests yet" />
        <StatCard title="Best Rank" value="--" desc="Attempt tests first" />
        <StatCard title="Study Time" value="0h" desc="Learning time" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_420px]">
        <div className="rounded-[36px] bg-white p-6 shadow-sm">
          <h3 className="text-2xl font-black text-slate-950">
            Recent Test Reports
          </h3>

          <div className="mt-5 rounded-[28px] border border-dashed border-slate-300 bg-[#f6f9ff] p-8 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-3xl shadow-sm">
              📊
            </div>
            <h4 className="mt-4 text-xl font-black text-slate-950">
              No test report yet
            </h4>
            <p className="mt-2 text-sm font-semibold text-slate-500">
              Test attempt karne ke baad reports yaha dikhenge.
            </p>
          </div>
        </div>

        <div className="rounded-[36px] bg-white p-6 shadow-sm">
          <p className="text-sm font-black uppercase tracking-wide text-blue-600">
            Mastery Score
          </p>

          <div className="mt-6 flex justify-center">
            <RingStat value={completionPercent} label="Profile Ready" />
          </div>

          <div className="mt-6 space-y-4">
            <ProgressLine label="Assignments Completed" value={0} />
            <ProgressLine label="Concepts Mastered" value={0} />
            <ProgressLine label="Profile Completed" value={completionPercent} />
          </div>
        </div>
      </div>
    </div>
  );
};

const SettingsTab = ({ logoutUser }: { logoutUser: () => void }) => {
  return (
    <div className="space-y-6">
      <PageHeader
        label="Settings"
        title="Account Settings"
        desc="Notifications aur account actions yaha manage karein."
      />

      <div className="grid gap-5 xl:grid-cols-2">
        <div className="rounded-[36px] bg-white p-6 shadow-sm">
          <h3 className="text-2xl font-black text-slate-950">
            Notification Preferences
          </h3>

          <div className="mt-5 space-y-3">
            {[
              "Live class reminders",
              "Test and quiz alerts",
              "New study notes updates",
              "Payment and course updates",
            ].map((item) => (
              <label
                key={item}
                className="flex items-center justify-between rounded-2xl bg-[#f6f9ff] p-4"
              >
                <span className="text-sm font-bold text-slate-700">{item}</span>
                <input
                  type="checkbox"
                  defaultChecked
                  className="h-5 w-5 accent-blue-600"
                />
              </label>
            ))}
          </div>
        </div>

        <div className="rounded-[36px] border border-red-100 bg-red-50 p-6">
          <h3 className="text-2xl font-black text-red-600">Logout Account</h3>
          <p className="mt-2 text-sm font-semibold leading-6 text-red-500">
            Ye browser se login session remove karega.
          </p>
          <button
            onClick={logoutUser}
            className="mt-6 rounded-2xl bg-red-600 px-6 py-3 text-sm font-black text-white hover:bg-red-700"
          >
            Logout Now
          </button>
        </div>
      </div>
    </div>
  );
};

const KarmaLeaderboard = () => {
  const data = [
    { name: "Profile Updated", points: "135 points", active: false },
    { name: "Course Preview", points: "123 points", active: false },
    { name: "You", points: "113 points", active: true },
    { name: "Live Class Ready", points: "107 points", active: false },
  ];

  return (
    <div className="rounded-[36px] bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-black uppercase tracking-wide text-blue-600">
            Karma Leaderboard
          </p>
          <h3 className="mt-2 text-2xl font-black text-slate-950">
            Learning Activity
          </h3>
          <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">
            Learn, attend classes aur tests complete karke points badhenge.
          </p>
        </div>

        <div className="flex h-8 w-16 items-center rounded-full bg-sky-400 p-1">
          <span className="ml-auto flex h-6 w-6 items-center justify-center rounded-full bg-white text-xs font-black text-sky-500">
            ×
          </span>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {data.map((item, index) => (
          <div
            key={item.name}
            className={`flex items-center justify-between rounded-2xl px-4 py-3 text-sm font-black ${
              item.active
                ? "bg-sky-400 text-white"
                : "border border-slate-200 bg-white text-slate-500"
            }`}
          >
            <span>
              {index + 12}. {item.name}
            </span>
            <span>{item.points}</span>
          </div>
        ))}
      </div>

      <button className="mt-5 text-sm font-black text-slate-400 hover:text-blue-600">
        ⟳ Load More
      </button>
    </div>
  );
};

const MetricCard = ({
  icon,
  title,
  value,
  desc,
}: {
  icon: string;
  title: string;
  value: string;
  desc: string;
}) => {
  return (
    <div className="rounded-[28px] bg-white p-5 text-center shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border-4 border-sky-200 bg-sky-50 text-2xl">
        {icon}
      </div>
      <h3 className="mt-4 text-2xl font-black text-slate-950">{value}</h3>
      <p className="mt-1 text-sm font-black text-slate-700">{title}</p>
      <p className="mt-2 text-xs font-semibold text-slate-400">{desc}</p>
    </div>
  );
};

const RingStat = ({ value, label }: { value: number; label: string }) => {
  return (
    <div
      className="flex h-32 w-32 items-center justify-center rounded-full p-3"
      style={{
        background: `conic-gradient(#2563eb ${value * 3.6}deg, #facc15 ${
          value * 3.6
        }deg, #e5e7eb 0deg)`,
      }}
    >
      <div className="flex h-full w-full flex-col items-center justify-center rounded-full bg-white">
        <span className="text-3xl font-black text-sky-500">{value}%</span>
        <span className="mt-1 text-[10px] font-black uppercase text-slate-400">
          {label}
        </span>
      </div>
    </div>
  );
};

const ProgressLine = ({ label, value }: { label: string; value: number }) => {
  return (
    <div>
      <div className="mb-2 flex justify-between text-xs font-black text-slate-500">
        <span>{label}</span>
        <span>{value}%</span>
      </div>
      <div className="h-3 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-blue-600"
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
};

const InfoPill = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="rounded-2xl bg-white/70 px-4 py-3 shadow-sm backdrop-blur">
      <p className="text-[10px] font-black uppercase tracking-wide text-slate-400">
        {label}
      </p>
      <p className="mt-1 text-sm font-black text-slate-950">{value}</p>
    </div>
  );
};

const InfoCard = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="rounded-[24px] bg-[#f6f9ff] p-5">
      <p className="text-xs font-black uppercase tracking-wide text-slate-400">
        {label}
      </p>
      <p className="mt-2 break-words text-base font-black text-slate-950">
        {value}
      </p>
    </div>
  );
};

const StatCard = ({
  title,
  value,
  desc,
}: {
  title: string;
  value: string;
  desc: string;
}) => {
  return (
    <div className="rounded-[30px] bg-white p-6 shadow-sm">
      <p className="text-sm font-bold text-slate-500">{title}</p>
      <h2 className="mt-2 text-4xl font-black text-slate-950">{value}</h2>
      <p className="mt-2 text-xs font-black text-emerald-600">{desc}</p>
    </div>
  );
};

const TextInput = ({
  label,
  value,
  onChange,
  disabled = false,
  type = "text",
}: {
  label: string;
  value: string;
  disabled?: boolean;
  type?: string;
  onChange: (value: string) => void;
}) => {
  return (
    <div>
      <label className="text-xs font-black uppercase tracking-wide text-slate-500">
        {label}
      </label>
      <input
        type={type}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-2 h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm font-bold outline-none focus:border-blue-400 ${
          disabled ? "bg-slate-100 text-slate-500" : "bg-white text-slate-800"
        }`}
      />
    </div>
  );
};

const SelectInput = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) => {
  return (
    <div>
      <label className="text-xs font-black uppercase tracking-wide text-slate-500">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 outline-none focus:border-blue-400"
      >
        {options.map((item) => (
          <option key={item}>{item}</option>
        ))}
      </select>
    </div>
  );
};

const PageHeader = ({
  label,
  title,
  desc,
}: {
  label: string;
  title: string;
  desc: string;
}) => {
  return (
    <div className="rounded-[36px] bg-white p-6 shadow-sm">
      <p className="text-sm font-black uppercase tracking-wide text-blue-600">
        {label}
      </p>
      <h2 className="mt-2 text-3xl font-black tracking-[-0.04em] text-slate-950">
        {title}
      </h2>
      <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-slate-500">
        {desc}
      </p>
    </div>
  );
};

export default StudentProfile;